// PWA utilities for handling offline state and installation

export const isPWA = (): boolean => {
    return window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;
};

export const isOnline = (): boolean => {
    return navigator.onLine;
};

export const canInstallPWA = (): boolean => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
};

// PWA installation prompt handling
let deferredPrompt: any;

export const setupPWAInstallPrompt = () => {
    window.addEventListener('beforeinstallprompt', (e: any) => {
        // If the app wants to show a custom install UI, prevent default and stash the event.
        // Otherwise, allow the native banner to appear.
        const preferCustomUI = false; // set to true if adding a bespoke install button elsewhere
        if (preferCustomUI) {
            e.preventDefault();
            deferredPrompt = e;
        }
    });
};

export const showInstallPrompt = async (): Promise<boolean> => {
    if (!deferredPrompt) {
        return false;
    }

    // Show the prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    // We no longer need the prompt. Clear it up.
    deferredPrompt = null;

    return outcome === 'accepted';
};

// Service Worker registration with update handling
export const registerServiceWorker = async (): Promise<void> => {
    if (!('serviceWorker' in navigator)) {
        console.log('Service Worker not supported');
        return;
    }

    // Skip service worker registration in development or unsupported environments
    if (import.meta.env.DEV || window.location.hostname === 'localhost' ||
        window.location.hostname.includes('stackblitz') ||
        window.location.hostname.includes('webcontainer')) {
        console.log('SW: Skipping service worker registration in development/unsupported environment');
        return;
    }

    try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('SW registered: ', registration);

        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
                console.log('SW: New service worker found, updating...');
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        console.log('SW: New service worker installed, reloading page...');
                        // Force reload to get the new version
                        window.location.reload();
                    }
                });
            }
        });

        // Listen for controlling service worker changes
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('SW: Controller changed, reloading page...');
            window.location.reload();
        });

        // Check for updates every 5 minutes (only in production)
        if (!import.meta.env.DEV) {
            setInterval(() => {
                registration.update();
            }, 5 * 60 * 1000);
        }

    } catch (error) {
        console.error('SW registration failed: ', error);
    }
};

// Force clear all caches and reload
export const clearCacheAndReload = async (): Promise<void> => {
    if ('serviceWorker' in navigator) {
        // Unregister all service workers
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
            await registration.unregister();
        }
    }

    if ('caches' in window) {
        // Clear all caches
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Clear browser cache and reload
    window.location.reload();
};

// Initialize PWA features
export const initPWA = () => {
    setupPWAInstallPrompt();
    registerServiceWorker();

    // Handle online/offline status
    window.addEventListener('online', () => {
        console.log('App is back online');
        document.body.classList.remove('offline');
    });

    window.addEventListener('offline', () => {
        console.log('App is offline');
        document.body.classList.add('offline');
    });

    // Add keyboard shortcut to force refresh (Ctrl+Shift+R or Cmd+Shift+R)
    window.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
            e.preventDefault();
            clearCacheAndReload();
        }
    });
};
